import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { AuthShell, Field, authBtn, authInput } from "@/components/portal/AuthShell";

const CodeStep = ({ challenge, onBack }) => {
  const { verifyLogin } = useAdmin();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [remember, setRemember] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy("verify"); setError("");
    try { await verifyLogin(challenge.challenge, code, remember); navigate("/", { replace: true }); }
    catch (err) { setError(errText(err, "That code isn't right.")); if ([410, 429].includes(err?.response?.status) && /log in again/i.test(errText(err))) setTimeout(onBack, 2500); }
    finally { setBusy(""); }
  };
  const resend = async () => {
    setBusy("resend"); setError(""); setNote("");
    try { await api.post("/auth/login/resend", { challenge: challenge.challenge }); setNote(`A new code is on its way to ${challenge.email_hint}.`); setCode(""); }
    catch (err) { setError(errText(err)); } finally { setBusy(""); }
  };
  return (
    <AuthShell title="Check your email" subtitle={`For your security, we've sent a 6-digit code to ${challenge.email_hint}. Enter it below to finish logging in. It works for ${challenge.minutes} minutes.`} testId="login-code-page">
      <form onSubmit={submit} className="space-y-5" data-testid="login-code-form">
        <Field label="Login code"><input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${authInput} text-center font-mono text-2xl tracking-[0.5em]`} placeholder="000000" autoFocus data-testid="login-code-input" /></Field>
        <label className="flex cursor-pointer items-start gap-3 text-sm" data-testid="login-remember">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#E62314]" data-testid="login-remember-checkbox" />
          <span>Remember this device for 30 days<span className="block text-xs text-zinc-300">You won't need a code on this device. Only tick this on a device you own. You'll still need your password.</span></span>
        </label>
        <button type="submit" disabled={!!busy || code.length !== 6} className={authBtn} data-testid="login-code-submit">{busy === "verify" && <Loader2 className="h-4 w-4 animate-spin" />}Verify and log in</button>
        {error && <p className="text-sm font-semibold text-[#ff6b5e]" data-testid="login-code-error">{error}</p>}
        {note && <p className="text-sm text-zinc-300" data-testid="login-code-note">{note}</p>}
        <div className="flex flex-wrap justify-between gap-3 text-sm font-semibold text-zinc-300">
          <button type="button" onClick={resend} disabled={!!busy} className="underline-offset-4 hover:text-white hover:underline disabled:opacity-40" data-testid="login-code-resend">{busy === "resend" ? "Sending..." : "Send a new code"}</button>
          <button type="button" onClick={onBack} className="underline-offset-4 hover:text-white hover:underline" data-testid="login-code-back">Use a different account</button>
        </div>
      </form>
    </AuthShell>
  );
};

export default function Login() {
  const { login, user } = useAdmin();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);

  if (user) return <Navigate to="/" replace />;
  if (challenge) return <CodeStep challenge={challenge} onBack={() => { setChallenge(null); setPassword(""); }} />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.verify_required) setChallenge(res);
      else navigate("/", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError({ code: d?.code || "bad", message: errText(err, "Email or password is incorrect") });
    } finally { setBusy(false); }
  };
  const requestAccess = async () => {
    setBusy(true);
    try { await api.post("/auth/request-access", { email: email.trim() }); setRequested(true); } catch (err) { setError({ code: "locked", message: errText(err) }); } finally { setBusy(false); }
  };
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification", { email: email.trim() }); setRequested(true); } catch (err) { setError({ code: "email_unverified", message: errText(err) }); } finally { setBusy(false); }
  };
  const sendReset = async () => {
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email: email.trim() }); setRequested(true); } catch (err) { setError({ code: "password_reset", message: errText(err) }); } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Log in" testId="login-page">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="Email address"><input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className={authInput} data-testid="login-email" required /></Field>
        <Field label="Password"><input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className={authInput} data-testid="login-password" required /></Field>
        <button type="submit" disabled={busy || !email || !password} className={authBtn} data-testid="login-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Log in</button>
        <Link to="/forgot-password" className="block text-center text-sm font-semibold text-zinc-300 underline-offset-4 hover:text-white hover:underline" data-testid="login-forgot-link">Forgot your password?</Link>
      </form>
      {error && (
        <div className="mt-6 border border-[#E62314]/60 bg-[#E62314]/10 p-4" data-testid={`login-error-${error.code}`}>
          <p className="text-sm font-semibold" data-testid="login-error-message">{error.message}</p>
          {error.code === "locked" && (requested
            ? <p className="mt-3 text-sm text-zinc-300" data-testid="login-access-requested">Your request has been sent. You'll be able to log in once the administrator reactivates your account.</p>
            : <button type="button" onClick={requestAccess} disabled={busy || !email} className="mt-3 w-full border border-white/40 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white hover:text-black" data-testid="login-request-access">Request Access to This Account</button>)}
          {error.code === "email_unverified" && (requested
            ? <p className="mt-3 text-sm text-zinc-300" data-testid="login-verify-sent">A new verification link is on its way to {email.trim()}. It works for 24 hours.</p>
            : <button type="button" onClick={resend} disabled={busy || !email} className="mt-3 w-full border border-white/40 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white hover:text-black" data-testid="login-resend-verify">Send a new verification link</button>)}
          {error.code === "password_reset" && (requested
            ? <p className="mt-3 text-sm text-zinc-300" data-testid="login-reset-sent">A new password link is on its way to {email.trim()}. It works for 15 minutes.</p>
            : <button type="button" onClick={sendReset} disabled={busy || !email} className="mt-3 w-full border border-white/40 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-white hover:text-black" data-testid="login-send-reset">Send a new password link</button>)}
        </div>
      )}
    </AuthShell>
  );
}
